import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Popup } from '@/components/Popup';
import { Button } from '@/components/ui/Button';
import type { LeagueDisableImpact } from '@/types/league';
import styles from './styles.module.scss';

export interface LeagueDisableConfirmProps {
  /** Impact of the league being disabled; the dialog is open while set. */
  impact: LeagueDisableImpact | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation before disabling a league that still has non-archived
 * tournaments or unfinished matches: lists what is affected and what
 * disabling does. Rendered through `Popup` (portal), so it works inside
 * swipeable / overflow-hidden containers.
 */
export const LeagueDisableConfirm: React.FC<LeagueDisableConfirmProps> = ({ impact, onConfirm, onCancel }) => {
  const { t } = useTranslation('admin');

  if (!impact) return null;

  // The API lists only the largest tournaments; the rest are counted.
  const visible = impact.tournaments;
  const hiddenCount = impact.tournamentCount - visible.length;

  return (
    <Popup open onClose={onCancel} hideCloseButton>
      <div className={styles.dialog} role="alertdialog" aria-labelledby="league-disable-confirm-title" data-testid="league-disable-confirm">
        <h2 id="league-disable-confirm-title" className={styles.title}>
          {t('admin:leagueDisableTitle', { name: impact.leagueName })}
        </h2>

        {impact.tournamentCount > 0 && (
          <section className={styles.section}>
            <p className={styles.sectionTitle}>
              {t('admin:leagueDisableTournaments', { count: impact.tournamentCount })}
            </p>
            <ul className={styles.tournaments}>
              {visible.map((tournament) => (
                <li key={tournament.id} className={styles.tournament}>
                  <Link to={`/tournaments/${tournament.id}`} className={styles.tournamentLink}>
                    {tournament.name}
                  </Link>
                  <span className={styles.participants}>
                    {t('admin:leagueDisableParticipants', { count: tournament.participantCount })}
                  </span>
                </li>
              ))}
            </ul>
            {hiddenCount > 0 && (
              <p className={styles.more}>{t('admin:leagueDisableMoreTournaments', { count: hiddenCount })}</p>
            )}
          </section>
        )}

        {(impact.upcomingMatches > 0 || impact.unresolvedMatches > 0) && (
          <ul className={styles.matches}>
            {impact.upcomingMatches > 0 && (
              <li data-testid="league-disable-confirm-upcoming">
                {t('admin:leagueDisableUpcomingMatches', { count: impact.upcomingMatches })}
              </li>
            )}
            {impact.unresolvedMatches > 0 && (
              <li data-testid="league-disable-confirm-unresolved">
                {t('admin:leagueDisableUnresolvedMatches', { count: impact.unresolvedMatches })}
              </li>
            )}
          </ul>
        )}

        <p className={styles.consequence}>{t('admin:leagueDisableConsequence')}</p>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            data-testid="league-disable-confirm-cancel"
          >
            {t('admin:leagueDisableCancel')}
          </Button>
          <Button
            type="button"
            color="decline"
            onClick={onConfirm}
            data-testid="league-disable-confirm-submit"
          >
            {t('admin:leagueDisableSubmit')}
          </Button>
        </div>
      </div>
    </Popup>
  );
};

export default LeagueDisableConfirm;
